import { useState, useRef, useCallback } from 'react';
import { X, Download, Share2, Copy, Check, Loader2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';

const CARD_STYLES = [
  {
    id: 'sunset',
    name: 'Sunset Temple',
    bg: 'bg-gradient-to-br from-orange-700 via-amber-600 to-orange-800',
    text: 'text-white',
    sub: 'text-orange-100',
    accent: 'text-amber-200',
    border: 'border-amber-400/30',
    om: 'text-white/10',
  },
  {
    id: 'sacred',
    name: 'Sacred Night',
    bg: 'bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900',
    text: 'text-white',
    sub: 'text-purple-200',
    accent: 'text-amber-300',
    border: 'border-purple-400/20',
    om: 'text-white/8',
  },
  {
    id: 'lotus',
    name: 'Lotus White',
    bg: 'bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50',
    text: 'text-orange-900',
    sub: 'text-orange-700',
    accent: 'text-orange-600',
    border: 'border-orange-200',
    om: 'text-orange-200/30',
  },
  {
    id: 'forest',
    name: 'Forest Peace',
    bg: 'bg-gradient-to-br from-emerald-800 via-teal-700 to-green-900',
    text: 'text-white',
    sub: 'text-emerald-100',
    accent: 'text-amber-300',
    border: 'border-emerald-400/20',
    om: 'text-white/8',
  },
];

export default function DharmaShareCard({ quote, onClose }) {
  const [style, setStyle] = useState(CARD_STYLES[0]);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return null;
    setGenerating(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        cacheBust: true,
      });
      return dataUrl;
    } catch (err) {
      console.error('Image generation error:', err);
      toast.error('Failed to generate image');
      return null;
    } finally {
      setGenerating(false);
    }
  }, []);

  const handleDownload = async () => {
    const dataUrl = await generateImage();
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.download = `daily-dharma-${quote?.id || 'card'}.png`;
    link.href = dataUrl;
    link.click();
    toast.success('Image saved!');
  };

  const handleShare = async () => {
    const dataUrl = await generateImage();
    if (!dataUrl) return;

    // Convert data URL to blob
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const file = new File([blob], 'daily-dharma.png', { type: 'image/png' });

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          title: 'Daily Dharma',
          text: `"${quote?.sanskrit}"\n${quote?.meaning}\n- ${quote?.source}\n\nShared from DharmBook`,
          files: [file],
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Sharing failed');
        }
      }
    } else {
      // Fallback: copy text
      handleCopyText();
    }
  };

  const handleCopyText = () => {
    const text = `"${quote?.sanskrit}"\n\n${quote?.transliteration}\n\n${quote?.meaning}\n\nSource: ${quote?.source}\n\nShared from DharmBook`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Quote copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!quote) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="share-modal">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
          <h3 className="font-bold text-slate-800 text-lg">Share Daily Dharma</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded-full transition-colors" data-testid="share-close-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Card Preview */}
          <div ref={cardRef} className={`${style.bg} rounded-2xl p-6 relative overflow-hidden`} style={{ minHeight: '280px' }}>
            {/* Decorative Om */}
            <div className={`absolute -top-6 -right-6 text-[140px] leading-none ${style.om} select-none pointer-events-none`} style={{ fontFamily: 'Noto Serif Devanagari, serif' }}>
              ॐ
            </div>
            {/* Decorative dots */}
            <div className={`absolute bottom-4 left-4 flex gap-1 ${style.om}`}>
              {[...Array(5)].map((_, i) => <div key={i} className="w-1 h-1 rounded-full bg-current" />)}
            </div>

            <div className="relative z-10">
              {/* DharmBook badge */}
              <div className="flex items-center gap-2 mb-5">
                <div className={`w-7 h-7 rounded-lg ${style.id === 'lotus' ? 'bg-orange-500' : 'bg-white/20'} flex items-center justify-center`}>
                  <span className={`text-xs font-bold ${style.id === 'lotus' ? 'text-white' : 'text-white'}`}>DB</span>
                </div>
                <span className={`text-xs font-semibold tracking-widest uppercase ${style.accent}`}>Daily Dharma</span>
              </div>

              {/* Sanskrit Quote */}
              <p className={`text-2xl font-bold mb-3 leading-snug ${style.text}`} style={{ fontFamily: 'Noto Serif Devanagari, serif' }}>
                " {quote.sanskrit} "
              </p>

              {/* Transliteration */}
              <p className={`text-sm italic mb-2 ${style.sub}`}>{quote.transliteration}</p>

              {/* Divider */}
              <div className={`w-12 h-0.5 rounded-full mb-3 ${style.id === 'lotus' ? 'bg-orange-300' : 'bg-white/30'}`} />

              {/* Meaning */}
              <p className={`text-base font-medium mb-4 ${style.text}`}>{quote.meaning}</p>

              {/* Source */}
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium ${style.sub}`}>{quote.source}</span>
                <span className={`text-[10px] font-semibold tracking-wider ${style.sub} opacity-60`}>dharmbook.com</span>
              </div>
            </div>
          </div>

          {/* Style Picker */}
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Card Style</p>
            <div className="flex gap-2">
              {CARD_STYLES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStyle(s)}
                  className={`flex-1 py-2 px-3 rounded-xl text-xs font-medium transition-all ${
                    style.id === s.id
                      ? 'ring-2 ring-orange-500 ring-offset-2 shadow-md'
                      : 'hover:shadow-sm'
                  } ${s.bg} ${s.text}`}
                  data-testid={`style-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="border-t border-stone-100 px-5 py-4 flex items-center gap-3">
          <button
            onClick={handleCopyText}
            className="flex items-center gap-2 px-4 py-2.5 border border-stone-200 rounded-xl text-slate-700 font-medium text-sm hover:bg-stone-50 transition-colors"
            data-testid="copy-text-btn"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Text'}
          </button>
          <button
            onClick={handleDownload}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2.5 border border-stone-200 rounded-xl text-slate-700 font-medium text-sm hover:bg-stone-50 transition-colors disabled:opacity-50"
            data-testid="download-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Save
          </button>
          <button
            onClick={handleShare}
            disabled={generating}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50 shadow-md"
            data-testid="share-btn-action"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
        </div>
      </div>
    </div>
  );
}
